<template>
  <div
    :id="uuid"
    :class="{
      'scrolled-to-end': scrolledToEnd,
      'scrolled-to-start': scrolledToStart,
      'has-sticky-column-right': stickyColumnRight,
    }"
    class="table-scroll-wrapper tw:overflow-auto"
  >
    <table :class="[$options.name, tableClassList]" :style="`table-layout: ${layout}; min-width: ${minWidth}`">
      <slot></slot>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, onBeforeUnmount, ref, watch } from "vue";

export default defineComponent({
  name: "EdroneTable",
  props: {
    /**
     * Create overlay on table to show loading state
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Sets the minimum width of the table. The table will be scrollable when the parent is smaller than the given value
     */
    minWidth: {
      type: String,
      default: "auto",
    },
    /**
     * Sets how the table should behave.
     * If set to `fixed`, the table will divide the columns into equal widths unless you force the size on specific columns, then it will divide the remaining size across the remaining columns. If you set width on all columns, then table will divide the width by proportion.
     * If set to `auto` the table itself will adjust the column width to the content in the columns.
     */
    layout: {
      type: String as PropType<"auto" | "fixed">,
      default: "fixed",
    },
  },
  setup(props, { slots }) {
    const scrolledToEnd = ref(true);
    const scrolledToStart = ref(true);
    const stickyColumnRight = ref(false);
    const uuid = ref(`table-scroll-wrapper-${Math.random().toString(36).substring(7)}`);

    const tableClassList = computed(() => {
      return {
        "EdroneTable--loading": props.loading,
      };
    });

    watch(
      () => slots.default?.(),
      () => {
        setScrolled();
      }
    );

    onBeforeUnmount(() => {
      document.getElementById(uuid.value)?.removeEventListener("scroll", setScrolled);
      window.removeEventListener("resize", setScrolled);
    });

    function setScrolled() {
      setScrolledToEnd();
      setScrolledToStart();
    }

    function setScrolledToEnd() {
      const tableScrollWrapper = document.getElementById(uuid.value);
      if (tableScrollWrapper) {
        scrolledToEnd.value =
          tableScrollWrapper.scrollLeft + tableScrollWrapper.clientWidth >= tableScrollWrapper.scrollWidth;
      }
    }

    function setScrolledToStart() {
      const tableScrollWrapper = document.getElementById(uuid.value);
      if (tableScrollWrapper) {
        scrolledToStart.value = tableScrollWrapper.scrollLeft === 0;
      }
    }

    setTimeout(() => {
      setScrolled();
      document.getElementById(uuid.value)?.removeEventListener("scroll", setScrolled);
      document.getElementById(uuid.value)?.addEventListener("scroll", setScrolled);
      window.removeEventListener("resize", setScrolled);
      window.addEventListener("resize", setScrolled);
      const scr = document.getElementById(uuid.value)?.querySelectorAll(".sticky-column-right");
      stickyColumnRight.value = (scr && scr.length > 0) as boolean;
    }, 10);

    return {
      stickyColumnRight,
      uuid,
      scrolledToStart,
      scrolledToEnd,
      tableClassList,
    };
  },
});
</script>

<style>
@reference "vue-app/assets/scss/main.css";

.sticky-column-right {
  white-space-collapse: collapse !important;
  padding: 0 !important;
  width: 100px;
  overflow: initial !important;
}

.scrolled-to-end.scrolled-to-start .sticky-column-right {
  background: none;
}
.scrolled-to-end.scrolled-to-start .sticky-column-left {
  background: none;
}

.table-scroll-wrapper {
  position: relative;
  width: 100%;
}

.table-scroll-wrapper:not(.scrolled-to-start) {
  background: linear-gradient(to right, rgb(0 0 0 / 5%) 0, rgb(255 255 255) 10px, rgb(255 255 255 / 0%) 10%);
  border-left: 1px solid #f2f2f2;
}

@media (min-width: 1200px) {
  .table-scroll-wrapper:not(.scrolled-to-end) td.sticky-column-right:before {
    content: "";
    position: absolute;
    top: 0;
    left: -10px;
    width: 10px;
    height: 100%;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(0, 0, 0, 0.05));
  }

  .table-scroll-wrapper:not(.scrolled-to-end) td > div {
    border-left: 1px solid #f2f2f2;
  }

  .table-scroll-wrapper:not(.scrolled-to-end):not(.has-sticky-column-right) {
    background: linear-gradient(to left, rgb(0 0 0 / 5%) 0, rgb(255 255 255) 1%, rgb(255 255 255 / 0%) 10%);
    border-right: 1px solid #f2f2f2;
  }

  .table-scroll-wrapper:not(.scrolled-to-end):not(.scrolled-to-start):not(.has-sticky-column-right) {
    background: linear-gradient(
      to left,
      rgb(0 0 0 / 5%) 0,
      rgb(255 255 255) 1%,
      rgb(255 255 255 / 0%) 10%,
      rgb(255 255 255 / 0%) 90%,
      rgb(255 255 255) 99%,
      rgb(0 0 0 / 5%) 100%
    );
  }
}

@media (max-width: 1199px) {
  .sticky-column-right > div {
    border: none !important;
  }

  .table-scroll-wrapper:not(.scrolled-to-end) {
    background: linear-gradient(to left, rgb(0 0 0 / 5%) 0, rgb(255 255 255) 1%, rgb(255 255 255 / 0%) 10%);
    border-right: 1px solid #f2f2f2;
  }

  .table-scroll-wrapper:not(.scrolled-to-end):not(.scrolled-to-start) {
    background: linear-gradient(
      to left,
      rgb(0 0 0 / 5%) 0,
      rgb(255 255 255) 1%,
      rgb(255 255 255 / 0%) 10%,
      rgb(255 255 255 / 0%) 90%,
      rgb(255 255 255) 99%,
      rgb(0 0 0 / 5%) 100%
    );
    border-left: 1px solid #f2f2f2;
    border-right: 1px solid #f2f2f2;
  }
}

.EdroneTable {
  padding: 12px;
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;
}

.EdroneTable,
.EdroneTable * {
  font-family: "Roboto", sans-serif;
}

.EdroneTable--loading {
  position: relative;
}

.EdroneTable--loading:after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% - 24px);
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;
}

.EdroneTable th,
.EdroneTable td {
  padding: 12px;
}

.EdroneTable tr {
  border-bottom: 1px solid #e5e5e5;
}

.EdroneTable th {
  @apply tw:body-2 tw:!font-semibold;
  margin: 0;
}

.EdroneTable td {
  white-space: normal;
  @apply tw:body-2;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  @apply tw:text-default-700;
}

.EdroneTable tbody tr td {
  height: 65px;
}
</style>
